/**
 * 重大资产**列表行**（`AC-R4.2`）
 *
 * 依据：`AC-R4.2`（行显 名称 / 类型 / 估值(带币种) / 累计投入 / 回报率 / 关联数 / 归属人）·
 * 原型 `16-重大资产_原型.html:173–181`（宽屏六列，窄屏收成两列）·
 * 铁律 8（红=正）· 31（金额必带币种）。
 *
 * ## 🔴 三处只渲染、不自算
 *
 * | 列 | 来源 |
 * |---|---|
 * | 估值 | `estimatedValue`（`null` ⇒ 「未填估值」，**不补 0**） |
 * | 累计投入 | `returnView.investedCents`（服务端从腿现算） |
 * | 回报率 | `returnView.roiE4`（万分之一整数，端侧只加符号） |
 *
 * ## ⚠️ 已处置行**不画动作**（`AC-R8.3`）
 *
 * 只读行（`readOnly`）不渲染点击箭头、不带 hover 反馈 —— 让"点了没反应"变成一眼可见的状态，
 * 而不是"点了跳进去才发现按钮全都没了"。
 */

import { Box, Chip, ListItemButton, Stack, Typography } from '@mui/material';
import type { MajorAssetView } from '@jiaying/shared';
import { Amount } from './Amount.js';
import {
  assetSubtitle,
  loanCountTextOf,
  roiDirectionOf,
  roiTextOf,
  valueTextOf,
} from '../features/major-asset.js';

export interface MajorAssetRowProps {
  view: MajorAssetView;
  /** 点击整行（已处置行传 `undefined` ⇒ 不可点） */
  onClick?: () => void;
}

/**
 * 类型 → **分类色板下标**（`theme.jiaying.chart[n]`，token 层单源）。
 *
 * 🔴 不写字面色值（架构 §11 CI 必扫）：三类资产的图标底色只回答"这是哪一类"，
 * 取色板前三档即可 —— 房=首色、车=第三色、收藏=第四色（与原型观感一致）。
 */
const TYPE_CHART_INDEX: Record<string, number> = {
  property: 0,
  vehicle: 2,
  collectible: 3,
};

export function MajorAssetRow({ view, onClick }: MajorAssetRowProps) {
  const direction = roiDirectionOf(view);
  const roiColor =
    direction === 0 ? 'text.secondary' : direction > 0 ? 'success.main' : 'error.main';
  const disposed = view.disposedDate !== null;

  return (
    <ListItemButton
      data-testid={`ma-row-${view.id}`}
      disabled={onClick === undefined}
      onClick={onClick}
      sx={{
        display: 'grid',
        // 两段式：名称列弹性 + 数值列定宽；窄屏只留估值与回报率
        gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0,1fr) auto auto auto auto' },
        columnGap: 2,
        rowGap: 0.5,
        alignItems: 'center',
        borderRadius: 1,
        opacity: disposed ? 0.72 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            flex: '0 0 30px',
            borderRadius: 1,
            bgcolor: (t) =>
              disposed
                ? t.palette.action.disabledBackground
                : t.jiaying.chart[TYPE_CHART_INDEX[view.type] ?? 0],
            color: 'common.white',
            display: 'grid',
            placeItems: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {view.name.slice(0, 1)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
              {view.name}
            </Typography>
            {view.hidden && (
              <Chip size="small" label="已隐藏" data-testid={`ma-hidden-tag-${view.id}`} />
            )}
            {disposed && (
              <Chip
                size="small"
                color="default"
                label={`${view.disposedDate ?? ''} 处置`}
                data-testid={`ma-disposed-tag-${view.id}`}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {assetSubtitle(view)}
          </Typography>
        </Box>
      </Stack>

      {/* 估值（必显；`null` 时是文字「未填估值」——不是 `¥0.00`） */}
      <Box sx={{ textAlign: 'right', minWidth: 104 }}>
        {view.estimatedValue === null ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`ma-value-empty-${view.id}`}
          >
            {valueTextOf(view)}
          </Typography>
        ) : (
          <Amount
            cents={view.estimatedValue as never}
            currency={view.currency}
            colored={false}
            variant="body2"
            sx={{ fontWeight: 600 }}
          />
        )}
        <Typography variant="caption" color="text.secondary" component="div">
          当前估值
        </Typography>
      </Box>

      {/* 累计投入（窄屏收起） */}
      <Box sx={{ textAlign: 'right', minWidth: 100, display: { xs: 'none', md: 'block' } }}>
        <Amount
          cents={view.returnView.investedCents as never}
          currency={view.currency}
          colored={false}
          variant="body2"
        />
        <Typography variant="caption" color="text.secondary" component="div">
          累计投入
        </Typography>
      </Box>

      {/* 回报率（必显） */}
      <Box sx={{ textAlign: 'right', minWidth: 76 }}>
        <Typography
          variant="body2"
          sx={{ color: roiColor, fontWeight: 600 }}
          data-testid={`ma-roi-${view.id}`}
        >
          {roiTextOf(view)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          回报率
        </Typography>
      </Box>

      {/* 关联数（窄屏收起） */}
      <Box sx={{ textAlign: 'right', minWidth: 72, display: { xs: 'none', md: 'block' } }}>
        <Typography variant="body2" color="text.secondary">
          {loanCountTextOf(view.loanCount)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          关联房贷
        </Typography>
      </Box>
    </ListItemButton>
  );
}
